import React, { useState, useEffect, useCallback } from "react";
import {
  Card, Row, Col, Statistic, Spin, Empty, Select, DatePicker, Segmented,
  Typography, Table, Tag, Tooltip as AntTooltip, message, Modal, Image, Button,
  Descriptions, Alert, Space,
} from "antd";
import { PictureOutlined } from "@ant-design/icons";
import {
  ResponsiveContainer, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip,
  LineChart, Line, Cell,
} from "recharts";
import dayjs from "dayjs";
import {
  getDoorCycleAnalytics, getDoorCycleTimeseries, getDoorCycleCycles, getCameras,
  getDoorCycleFrames,
} from "../network/api";
import InfoHint from "./common/InfoHint";

const { Title, Text } = Typography;
const { RangePicker } = DatePicker;

const GOOD = "#52c41a";
const WARN = "#faad14";
const CRIT = "#ff4d4f";
const ACCENT = "#13a8a8";
const MUTED = "#8c9bab";

// ms -> short human string ("0.8s", "820ms", "1.2s"), or "—" when null.
const fmtMs = (ms) => {
  if (ms == null) return "—";
  if (ms < 1000) return `${Math.round(ms)}ms`;
  return `${(ms / 1000).toFixed(1)}s`;
};
const pct = (x) => (x == null ? "—" : `${Math.round(x * 100)}%`);
const rateColor = (r) => (r == null ? MUTED : r >= 0.75 ? GOOD : r >= 0.4 ? WARN : CRIT);

// The picker is day-granular, so every query covers WHOLE days: the start day
// from 00:00:00.000 and the end day through 23:59:59.999. Without this a
// same-day pick sends from === to (a zero-width window -> always empty), and a
// "7d" preset anchored on the current clock time never matches the same dates
// picked by hand.
const dayWindow = (range) => ({
  from: range?.[0]?.startOf("day").toISOString(),
  to: range?.[1]?.endOf("day").toISOString(),
});

const DoorAnalytics = () => {
  // Device is an OPTIONAL filter (null = whole fleet), just like the date range.
  const [deviceId, setDeviceId] = useState(null);
  const [range, setRange] = useState([dayjs().subtract(7, "day").startOf("day"), dayjs().endOf("day")]);
  const [type, setType] = useState("daily");
  const [cameras, setCameras] = useState([]);
  const [analytics, setAnalytics] = useState(null);
  const [series, setSeries] = useState([]);
  const [cycles, setCycles] = useState([]);
  const [cycleTotal, setCycleTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [loading, setLoading] = useState(false);
  const [cyclesLoading, setCyclesLoading] = useState(false);

  // Device dropdown options (one-off).
  useEffect(() => {
    getCameras(1, 1000, {})
      .then((list) => setCameras(Array.isArray(list) ? list : []))
      .catch(() => setCameras([]));
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    const opts = { deviceId: deviceId || undefined, ...dayWindow(range) };
    try {
      const [a, ts] = await Promise.all([
        getDoorCycleAnalytics(opts),
        getDoorCycleTimeseries({ ...opts, type }),
      ]);
      setAnalytics(a || null);
      setSeries(Array.isArray(ts?.data) ? ts.data : []);
    } catch (err) {
      console.error("Failed to load door analytics", err);
      message.error("Could not load door analytics");
      setAnalytics(null); setSeries([]);
    } finally {
      setLoading(false);
    }
  }, [deviceId, range, type]);

  useEffect(() => { load(); }, [load]);

  // Cycles paginate against the server, so the table isn't capped at whatever
  // the first page happened to fetch. Kept separate from `load` so paging
  // doesn't re-run the (much heavier) analytics + timeseries scans.
  useEffect(() => {
    let stale = false; // paging fast can land responses out of order
    setCyclesLoading(true);
    getDoorCycleCycles({
      deviceId: deviceId || undefined,
      ...dayWindow(range),
      page,
      limit: pageSize,
    })
      .then((cy) => {
        if (stale) return;
        setCycles(Array.isArray(cy?.data) ? cy.data : []);
        setCycleTotal(Number.isFinite(cy?.total) ? cy.total : 0);
      })
      .catch((err) => {
        if (stale) return;
        console.error("Failed to load door cycles", err);
        setCycles([]); setCycleTotal(0);
      })
      .finally(() => { if (!stale) setCyclesLoading(false); });
    return () => { stale = true; };
  }, [deviceId, range, page, pageSize]);

  // --- photos for one door-open -------------------------------------------------
  // Fetched only when a row is opened: resolving a burst to its stored photos costs a
  // lookup per frame, and the table renders hundreds of rows nobody clicks.
  const [framesFor, setFramesFor] = useState(null);   // the cycle row whose modal is open
  const [framesData, setFramesData] = useState(null);
  const [framesLoading, setFramesLoading] = useState(false);
  const [framesError, setFramesError] = useState(null);

  const openFrames = useCallback(async (row) => {
    setFramesFor(row);
    setFramesData(null);
    setFramesError(null);
    setFramesLoading(true);
    try {
      setFramesData(await getDoorCycleFrames(row._id));
    } catch (err) {
      setFramesError(err?.response?.data?.error || err.message || "Could not load photos");
    } finally {
      setFramesLoading(false);
    }
  }, []);

  const t = analytics?.tiles || {};
  const tm = analytics?.timeMatrix || {};
  const hasData = (t.opens || 0) > 0;

  const dwellData = (analytics?.dwellBuckets || []).map((b) => ({
    label: b.label,
    ratePct: b.capturedRate == null ? 0 : Math.round(b.capturedRate * 100),
    rate: b.capturedRate,
    opens: b.opens,
  }));
  const firstFrameHist = (analytics?.firstFrameHistogram || []).map((h) => ({ label: h.label, count: h.count }));
  const interFrameHist = (analytics?.interFrameHistogram || []).map((h) => ({ label: h.label, count: h.count }));

  // Per-device capture rate. Only meaningful fleet-wide — a single-device filter
  // collapses this to one row/bar, so the whole section is hidden in that case.
  const deviceRateBuckets = analytics?.deviceRateBuckets || [];
  const deviceBreakdown = analytics?.deviceBreakdown || [];
  const showDeviceBreakdown = !deviceId && deviceBreakdown.length > 0;

  // Bands run high (green) to low (red); colour the bar by where the band sits.
  const bandColor = (label) =>
    label === "≥95%" || label === "90–95%" ? GOOD
      : label === "80–90%" || label === "70–80%" ? WARN
        : CRIT;

  // Likely-cause tag colour: hardware/sensor faults are red (need a field visit),
  // capture-side tuning is amber, an unclear case is neutral.
  const reasonColor = (code) =>
    code === "phantom_gyro" || code === "timer_fallback" ? "red"
      : code === "slow_first_frame" || code === "single_frame" ? "orange"
        : "default";

  // Recharts tooltip that names the devices in the hovered band, each with its own
  // rate — that's the "hover to see which device IDs" the band chart is for.
  const DeviceBandTooltip = ({ active, payload }) => {
    if (!active || !payload?.length) return null;
    const b = payload[0].payload;
    const devices = b.devices || [];
    if (!devices.length) return null;
    const shown = devices.slice(0, 12);
    return (
      <div style={{ background: "#14201f", border: "1px solid #2b3a3f", borderRadius: 6, padding: "8px 10px", maxWidth: 280 }}>
        <div style={{ color: "#fff", fontWeight: 600, marginBottom: 5 }}>
          {b.label} · {b.deviceCount} device{b.deviceCount === 1 ? "" : "s"}
        </div>
        {shown.map((d) => (
          <div key={d.deviceId} style={{ fontSize: 12, color: "#cbd5d1", display: "flex", justifyContent: "space-between", gap: 14 }}>
            <span>{d.deviceId}</span>
            <span style={{ color: rateColor(d.capturedRate), whiteSpace: "nowrap" }}>{pct(d.capturedRate)} · {d.opens} opens</span>
          </div>
        ))}
        {devices.length > shown.length && (
          <div style={{ fontSize: 11, color: MUTED, marginTop: 4 }}>+{devices.length - shown.length} more…</div>
        )}
      </div>
    );
  };

  const deviceBreakdownCols = [
    { title: "Device", dataIndex: "deviceId", key: "deviceId", render: (v) => <Text code>{v}</Text> },
    {
      title: <span>Capture rate<InfoHint title="Share of this device's door-opens that produced at least one usable photo, in the selected range." /></span>,
      dataIndex: "capturedRate", key: "capturedRate",
      defaultSortOrder: "ascend",
      sorter: (a, b) => (a.capturedRate ?? 1) - (b.capturedRate ?? 1),
      render: (v) => <Tag color={v == null ? "default" : v >= 0.75 ? "green" : v >= 0.4 ? "orange" : "red"}>{pct(v)}</Tag>,
    },
    {
      title: <span>Release<InfoHint title="The agent release this device is currently on. A low capture rate that lines up with one release points at a regression rather than a hardware fault." /></span>,
      dataIndex: "release", key: "release",
      sorter: (a, b) => String(a.release || "").localeCompare(String(b.release || "")),
      render: (v, r) =>
        v ? <AntTooltip title={r.releaseSha ? `sha ${r.releaseSha}…` : undefined}><Tag>{v}</Tag></AntTooltip> : <Text type="secondary">—</Text>,
    },
    { title: "Opens", dataIndex: "opens", key: "opens", sorter: (a, b) => a.opens - b.opens, render: (v) => v.toLocaleString() },
    { title: "Missed", key: "missed", sorter: (a, b) => (a.opens - a.captured) - (b.opens - b.captured), render: (_, r) => (r.opens - r.captured).toLocaleString() },
    { title: <span>Frames / open<InfoHint title="Average photos captured per door-open for this device." /></span>, dataIndex: "avgFramesPerOpen", key: "avgFramesPerOpen", render: (v) => (v ? v.toFixed(2) : "0") },
    {
      title: <span>Likely cause<InfoHint title="A best-guess reason for a low capture rate, inferred from the door-cycle signature (flood of ultra-short opens = likely faulty gyro; slow first frame = capture cold-start; timer-suppressed = gyro already bypassed). Hover a tag for the evidence. A hint to investigate, not a verdict." /></span>,
      dataIndex: "reason", key: "reason",
      render: (v, r) =>
        v ? (
          <AntTooltip title={r.reasonDetail}>
            <Tag color={reasonColor(r.reasonCode)} style={{ whiteSpace: "normal" }}>{v}</Tag>
          </AntTooltip>
        ) : (
          <Text type="secondary">—</Text>
        ),
    },
  ];

  const matrixStages = [
    ["Swing", "motion → open", tm.swing, "Time from the motion sensor first detecting movement to the door actually opening."],
    ["1st frame", "open → capture", tm.firstFrame, "Time from the door opening to the first photo being captured."],
    ["Inter-frame", "frame → frame", tm.interFrame, "Typical gap between consecutive captured frames while the door is open."],
    ["Dwell", "open → close", tm.dwell, "How long the door stayed open — from open to close."],
    ["Upload", "capture → server", tm.upload, "Time from capturing a photo to it arriving on the server."],
  ];

  const cycleCols = [
    {
      title: "Open (UTC)", dataIndex: "openAt", key: "openAt",
      render: (v) => (v ? dayjs(v).format("MM/DD HH:mm:ss") : "—"),
    },
    ...(!deviceId
      ? [{ title: "Device", dataIndex: "deviceId", key: "deviceId", render: (v) => <Text code>{v}</Text> }]
      : []),
    { title: <span>Dwell<InfoHint title="How long the door stayed open for this event." /></span>, dataIndex: "dwellMs", key: "dwellMs", render: fmtMs },
    { title: <span>1st frame<InfoHint title="Time from the door opening to the first photo for this event." /></span>, dataIndex: "firstFrameLatMs", key: "firstFrameLatMs", render: fmtMs },
    { title: <span>Frames<InfoHint title="Number of photos captured during this door-open." /></span>, dataIndex: "frameCount", key: "frameCount" },
    { title: <span>Avg gap<InfoHint title="Median gap between frames for this event (inter-frame time)." /></span>, dataIndex: "interFrameP50Ms", key: "interFrameP50Ms", render: fmtMs },
    {
      title: <span>Result<InfoHint title="Outcome of the open: captured (got a photo), missed (none), or suppressed (deliberately skipped, e.g. duplicate or filtered)." /></span>, key: "result",
      render: (_, r) =>
        r.suppressed ? <Tag color="default">{r.suppressed}</Tag>
          : r.captured ? <Tag color="green">captured</Tag>
            : <Tag color="red">missed</Tag>,
    },
    {
      title: "Photos", key: "photos",
      render: (_, r) =>
        r.frameCount ? (
          <Button size="small" icon={<PictureOutlined />} onClick={() => openFrames(r)}>
            {r.frameCount}
          </Button>
        ) : (
          <Text type="secondary">—</Text>
        ),
    },
  ];

  return (
    <div style={{ padding: 16 }}>
      <Row justify="space-between" align="middle" gutter={[12, 12]} style={{ marginBottom: 16 }}>
        <Col>
          <Title level={3} style={{ margin: 0 }}>Door Analytics<InfoHint iconStyle={{ fontSize: 15 }} title="Deep-dive into the chiller-door capture lifecycle: every time a door opens the device tries to photograph the shelf. These charts show how reliably and how quickly that happens, and where time is lost. Filter by device and date range above." /></Title>
          <Text type="secondary">
            {deviceId
              ? `Chiller-door capture lifecycle · Device ${deviceId}`
              : `Chiller-door capture lifecycle · Fleet${analytics?.deviceCount ? ` (${analytics.deviceCount} devices)` : ""}`}
          </Text>
        </Col>
        <Col>
          <Row gutter={[8, 8]} align="middle">
            <Col>
              <Select
                allowClear
                showSearch
                placeholder="All devices"
                style={{ width: 210 }}
                value={deviceId}
                onChange={(v) => { setDeviceId(v || null); setPage(1); }}
                optionFilterProp="label"
                options={cameras.map((c) => ({
                  value: c.deviceId,
                  label: `${c.deviceId}${c?.config?.title ? ` · ${c.config.title}` : ""}`,
                }))}
              />
            </Col>
            <Col>
              <RangePicker
                allowClear={false}
                value={range}
                onChange={(v) => { if (v) { setRange(v); setPage(1); } }}
                presets={[
                  { label: "Today", value: [dayjs().startOf("day"), dayjs().endOf("day")] },
                  { label: "7d", value: [dayjs().subtract(7, "day").startOf("day"), dayjs().endOf("day")] },
                  { label: "30d", value: [dayjs().subtract(30, "day").startOf("day"), dayjs().endOf("day")] },
                ]}
              />
            </Col>
          </Row>
        </Col>
      </Row>

      <Spin spinning={loading}>
        {/* ---- headline tiles ---- */}
        <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
          <Col xs={12} md={8} lg={4}>
            <Card><Statistic title={<span>Capture rate<InfoHint title="Share of door-opens that produced at least one usable photo. Higher is better — green ≥ 75%, amber 40–75%, red below 40%." /></span>} value={t.capturedRate == null ? "—" : Math.round(t.capturedRate * 100)}
              suffix={t.capturedRate == null ? "" : "%"} valueStyle={{ color: rateColor(t.capturedRate) }} /></Card>
          </Col>
          <Col xs={12} md={8} lg={4}>
            <Card><Statistic title={<span>Door opens<InfoHint title="Total number of door-open events in the selected device/date range." /></span>} value={t.opens || 0} /></Card>
          </Col>
          <Col xs={12} md={8} lg={4}>
            <Card><Statistic title={<span>Frames / open<InfoHint title="Average number of photos captured per door-open." /></span>} value={t.avgFramesPerOpen ? t.avgFramesPerOpen.toFixed(2) : "0"} /></Card>
          </Col>
          <Col xs={12} md={8} lg={4}>
            <Card><Statistic title={<span>1st-frame latency<InfoHint title="Median time from the door opening to the first photo being captured. Lower means the camera reacts faster and misses fewer short opens." /></span>} value={fmtMs(t.medianFirstFrameLatMs)} valueStyle={{ color: ACCENT }} />
              <Text type="secondary" style={{ fontSize: 12 }}>median</Text></Card>
          </Col>
          <Col xs={12} md={8} lg={4}>
            <Card><Statistic title={<span>Inter-frame gap<InfoHint title="Median gap between consecutive captured frames while the door is open." /></span>} value={fmtMs(t.medianInterFrameMs)} valueStyle={{ color: ACCENT }} />
              <Text type="secondary" style={{ fontSize: 12 }}>median</Text></Card>
          </Col>
          <Col xs={12} md={8} lg={4}>
            <Card>
              <Statistic
                title={<span>Missed opens<InfoHint title="Door-opens where no photo was captured at all. These are the failures the capture pipeline is trying to eliminate." /></span>}
                value={t.missedOpens ?? 0}
                valueStyle={{ color: (t.missedOpens || 0) > 0 ? CRIT : GOOD }}
              />
              <Text type="secondary" style={{ fontSize: 11 }}>door opened, no photo</Text>
            </Card>
          </Col>
          <Col xs={12} md={8} lg={4}>
            <Card>
              <Statistic
                title={<span>Single-frame bursts<InfoHint title="Opens that produced only one photo, so there's no choice of frames to pick the best shot from — often a sign the capture window was too short." /></span>}
                value={t.singleFrameOpens ?? 0}
                valueStyle={{ color: (t.singleFrameOpens || 0) > 0 ? WARN : GOOD }}
              />
              <Text type="secondary" style={{ fontSize: 11 }}>nothing to choose between</Text>
            </Card>
          </Col>
          <Col xs={12} md={8} lg={4}>
            <Card><Statistic title={<span>Dwell<InfoHint title="Median time the door stayed open (open → close). Short dwell times are the hardest to capture in time." /></span>} value={fmtMs(t.medianDwellMs)} />
              <Text type="secondary" style={{ fontSize: 12 }}>median open→close</Text></Card>
          </Col>
        </Row>

        {!hasData && (
          <Card style={{ marginBottom: 16 }}>
            <Empty description="No door cycles in this range. Devices report these once the analytics agent (canary) is deployed." style={{ padding: "40px 0" }} />
          </Card>
        )}

        {/* ---- capture rate by device (fleet only) ---- */}
        {showDeviceBreakdown && (
          <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
            <Col xs={24} lg={10}>
              <Card
                title={<span>Devices by capture rate<InfoHint title="How the fleet spreads across capture-rate bands. Each bar is a count of devices; hover a bar to see which device IDs sit in that band and their individual rates. Use it to spot how many devices are underperforming, then read the table for exactly which ones." /></span>}
                style={{ height: "100%" }}
                extra={<Text type="secondary" style={{ fontSize: 12 }}>{deviceBreakdown.length} devices · hover for IDs</Text>}
              >
                <ResponsiveContainer width="100%" height={280}>
                  <BarChart data={deviceRateBuckets}>
                    <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#2b3a3f" />
                    <XAxis dataKey="label" tick={{ fontSize: 11 }} />
                    <YAxis allowDecimals={false} tick={{ fontSize: 11 }} label={{ value: "devices", angle: -90, position: "insideLeft", style: { fontSize: 11, fill: MUTED } }} />
                    <Tooltip cursor={{ fill: "rgba(255,255,255,0.04)" }} content={<DeviceBandTooltip />} />
                    <Bar dataKey="deviceCount" name="devices" radius={[3, 3, 0, 0]}>
                      {deviceRateBuckets.map((b, i) => <Cell key={i} fill={bandColor(b.label)} />)}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
                <Text type="secondary" style={{ fontSize: 12 }}>
                  Bars left of “70–80%” are the devices worth opening a ticket on.
                </Text>
              </Card>
            </Col>
            <Col xs={24} lg={14}>
              <Card
                title={<span>Capture rate per device<InfoHint title="Every device in range, worst capture rate first. Click a device in the filter above to drill into its lifecycle. Sort by any column." /></span>}
                style={{ height: "100%" }}
                extra={<Text type="secondary" style={{ fontSize: 12 }}>worst first</Text>}
              >
                <Table
                  size="small"
                  rowKey="deviceId"
                  columns={deviceBreakdownCols}
                  dataSource={deviceBreakdown}
                  onRow={(r) => ({
                    onClick: () => { setDeviceId(r.deviceId); setPage(1); },
                    style: { cursor: "pointer" },
                  })}
                  pagination={deviceBreakdown.length > 10 ? { pageSize: 10, size: "small", showSizeChanger: false } : false}
                  locale={{ emptyText: <Empty description="No devices" /> }}
                />
              </Card>
            </Col>
          </Row>
        )}

        {/* ---- time matrix ---- */}
        <Card title={<span>Time matrix — where each second goes<InfoHint title="Breaks the capture lifecycle into stages and shows how long each one takes. Each tile shows the median and p90 (the slowest 10% start above the p90 value). Use it to see which stage is the bottleneck." /></span>} style={{ marginBottom: 16 }}
          extra={<Text type="secondary" style={{ fontSize: 12 }}>median · p90 per stage</Text>}>
          <Row gutter={[12, 12]}>
            {matrixStages.map(([name, sub, s, hint]) => (
              <Col xs={12} sm={8} lg={4} key={name}>
                <div style={{ padding: "6px 2px" }}>
                  <Text type="secondary" style={{ fontSize: 11, textTransform: "uppercase", letterSpacing: ".04em" }}>{name}<InfoHint title={hint} iconStyle={{ fontSize: 11 }} /></Text>
                  <div style={{ fontSize: 20, fontWeight: 600, fontVariantNumeric: "tabular-nums", color: name === "1st frame" || name === "Inter-frame" ? ACCENT : undefined }}>
                    {fmtMs(s?.medianMs)}
                  </div>
                  <Text type="secondary" style={{ fontSize: 11 }}>p90 {fmtMs(s?.p90Ms)} · {sub}</Text>
                </div>
              </Col>
            ))}
          </Row>
        </Card>

        {/* ---- two charts ---- */}
        <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
          <Col xs={24} lg={12}>
            <Card title={<span>Capture rate by door-open duration<InfoHint title="Capture success grouped by how long the door was open. Very short opens are hardest to catch — that's where the pre-warm fix helps most. Bars are coloured by success rate." /></span>} style={{ height: "100%" }}
              extra={<AntTooltip title="Short opens are the ones the pre-warm fix rescues"><Text type="secondary" style={{ fontSize: 12 }}>root cause</Text></AntTooltip>}>
              {hasData ? (
                <div style={{ width: "100%", height: 300 }}>
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={dwellData}>
                      <CartesianGrid strokeDasharray="3 3" vertical={false} />
                      <XAxis dataKey="label" tick={{ fontSize: 11 }} />
                      <YAxis domain={[0, 100]} unit="%" tick={{ fontSize: 11 }} />
                      <Tooltip formatter={(v, n, p) => [`${v}%  (${p?.payload?.opens || 0} opens)`, "captured"]} />
                      <Bar dataKey="ratePct" name="Captured %" radius={[3, 3, 0, 0]}>
                        {dwellData.map((d, i) => <Cell key={i} fill={rateColor(d.rate)} />)}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              ) : <Empty style={{ padding: "70px 0" }} />}
            </Card>
          </Col>
          <Col xs={24} lg={12}>
            <Card title={<span>First-frame latency distribution<InfoHint title="How many door-opens fell into each first-frame latency bucket. A distribution shifted left (faster) means the camera is catching opens sooner." /></span>} style={{ height: "100%" }}>
              {hasData ? (
                <div style={{ width: "100%", height: 300 }}>
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={firstFrameHist}>
                      <CartesianGrid strokeDasharray="3 3" vertical={false} />
                      <XAxis dataKey="label" tick={{ fontSize: 11 }} />
                      <YAxis allowDecimals={false} tick={{ fontSize: 11 }} />
                      <Tooltip />
                      <Bar dataKey="count" name="Opens" fill={ACCENT} radius={[3, 3, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              ) : <Empty style={{ padding: "70px 0" }} />}
            </Card>
          </Col>
        </Row>

        {/* ---- trend ---- */}
        <Card title={<span>Capture rate over time<InfoHint title="Capture rate trend across the selected range. Watch for drops that line up with a deployment, outage or environmental change." /></span>} style={{ marginBottom: 16 }}
          extra={<Segmented size="small" options={[{ label: "Daily", value: "daily" }, { label: "Hourly", value: "hourly" }]} value={type} onChange={setType} />}>
          {series.length ? (
            <div style={{ width: "100%", height: 260 }}>
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={series.map((d) => ({ ...d, ratePct: d.capturedRate == null ? null : Math.round(d.capturedRate * 100) }))}>
                  <CartesianGrid strokeDasharray="3 3" vertical={false} />
                  <XAxis dataKey="period" tick={{ fontSize: 11 }} interval="preserveStartEnd" />
                  <YAxis domain={[0, 100]} unit="%" tick={{ fontSize: 11 }} />
                  <Tooltip formatter={(v, n, p) => [`${v}%  (${p?.payload?.opens || 0} opens)`, "captured"]} />
                  <Line type="monotone" dataKey="ratePct" name="Capture rate" stroke={ACCENT} strokeWidth={2} dot={{ r: 2 }} connectNulls />
                </LineChart>
              </ResponsiveContainer>
            </div>
          ) : <Empty description="No trend data" style={{ padding: "60px 0" }} />}
        </Card>

        {/* ---- recent cycles ---- */}
        <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
          <Col xs={24} lg={12}>
            <Card size="small" title={<span>Frames per door-open<InfoHint title="Distribution of how many photos each open produced. Look at the shape, not the average: many 0s and 1s (red/amber bars) mean opens are being missed or barely caught." /></span>}
              extra={<Text type="secondary" style={{ fontSize: 12 }}>shape, not average</Text>}>
              <ResponsiveContainer width="100%" height={220}>
                <BarChart data={analytics?.frameCountBuckets || []}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#2b3a3f" />
                  <XAxis dataKey="label" stroke={MUTED} />
                  <YAxis stroke={MUTED} allowDecimals={false} />
                  <Tooltip contentStyle={{ background: "#14201f", border: "1px solid #2b3a3f" }} />
                  <Bar dataKey="opens" name="opens" fill={ACCENT}>
                    {(analytics?.frameCountBuckets || []).map((b, i) => (
                      <Cell key={i} fill={b.label === "0" ? CRIT : b.label === "1" ? WARN : ACCENT} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
              <Text type="secondary" style={{ fontSize: 12 }}>
                A mean of 4 is healthy if most bursts land on 4, and broken if half return 1
                and half return 7.
              </Text>
            </Card>
          </Col>
          <Col xs={24} lg={12}>
            <Card size="small" title={<span>Capture rate by hour<InfoHint title="Capture success by hour of the shop's local day (PKT). Reveals time-of-day faults — glare, lights-off, shutters — that a daily average hides." /></span>}
              extra={<Text type="secondary" style={{ fontSize: 12 }}>shop clock (PKT)</Text>}>
              <ResponsiveContainer width="100%" height={220}>
                <BarChart data={(analytics?.byHour || []).filter((h) => h.opens > 0)}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#2b3a3f" />
                  <XAxis dataKey="hour" stroke={MUTED} />
                  <YAxis stroke={MUTED} domain={[0, 100]} unit="%" />
                  <Tooltip
                    contentStyle={{ background: "#14201f", border: "1px solid #2b3a3f" }}
                    formatter={(v, n, o) => [`${v}% of ${o?.payload?.opens} opens`, "captured"]}
                    labelFormatter={(h) => `${String(h).padStart(2, "0")}:00`}
                  />
                  <Bar dataKey={(h) => (h.capturedRate == null ? 0 : Math.round(h.capturedRate * 100))}
                    name="captured">
                    {(analytics?.byHour || []).filter((h) => h.opens > 0).map((h, i) => (
                      <Cell key={i}
                        fill={h.capturedRate == null ? MUTED : h.capturedRate >= 0.75 ? GOOD
                          : h.capturedRate >= 0.4 ? WARN : CRIT} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
              <Text type="secondary" style={{ fontSize: 12 }}>
                Glare, lights-off and shutters are time-of-day faults — a camera that only
                fails after dark looks fine in a daily average.
              </Text>
            </Card>
          </Col>
        </Row>

        <Card title={<span>Door cycles<InfoHint title="One row per door-open event, with its timing and whether a photo was captured. Use it to inspect individual opens behind the aggregate charts." /></span>} extra={<Text type="secondary" style={{ fontSize: 12 }}>{cycleTotal.toLocaleString()} in range</Text>}>
          <Table
            size="small"
            loading={cyclesLoading}
            rowKey={(r) => r._id || `${r.deviceId}-${r.openAt}`}
            columns={cycleCols}
            dataSource={cycles}
            pagination={{
              current: page,
              pageSize,
              total: cycleTotal,
              showSizeChanger: true,
              pageSizeOptions: [25, 50, 100, 200],
              showTotal: (total, [a, b]) => `${a}-${b} of ${total.toLocaleString()}`,
              onChange: (p, s) => { setPage(p); setPageSize(s); },
            }}
            locale={{ emptyText: <Empty description="No cycles" /> }}
          />
        </Card>
      </Spin>

      <Modal
        open={Boolean(framesFor)}
        onCancel={() => setFramesFor(null)}
        footer={null}
        width={980}
        title={
          framesFor
            ? `Photos from the door-open at ${dayjs(framesFor.openAt).format("MM/DD HH:mm:ss")}` +
              (framesFor.deviceId ? ` · ${framesFor.deviceId}` : "")
            : "Photos"
        }
      >
        {framesLoading && (
          <div style={{ textAlign: "center", padding: 40 }}><Spin /></div>
        )}
        {framesError && <Alert type="error" showIcon message={framesError} />}
        {!framesLoading && !framesError && framesData && (
          <>
            <Descriptions size="small" column={4} bordered style={{ marginBottom: 14 }}>
              <Descriptions.Item label="Captured">{framesData.summary?.captured ?? "—"}</Descriptions.Item>
              <Descriptions.Item label="Stored">{framesData.summary?.resolved ?? "—"}</Descriptions.Item>
              <Descriptions.Item label="Chiller seen">
                {framesData.summary?.chillerFound ?? "—"}
              </Descriptions.Item>
              <Descriptions.Item label="Best confidence">
                {framesData.summary?.bestConfidence != null
                  ? framesData.summary.bestConfidence.toFixed(3)
                  : "—"}
              </Descriptions.Item>
            </Descriptions>

            {/* Captured on the device but never arrived is a different fault from a burst
                that never fired, and the counters cannot tell them apart. */}
            {framesData.summary?.missing > 0 && (
              <Alert
                type="warning"
                showIcon
                style={{ marginBottom: 14 }}
                message={`${framesData.summary.missing} of ${framesData.summary.captured} frames never reached the server`}
                description="The device captured them; they are not in storage. That is an upload problem, not a capture one."
              />
            )}

            <Image.PreviewGroup>
              <Row gutter={[12, 12]}>
                {(framesData.frames || []).map((f) => (
                  <Col key={f.seq ?? f.file} xs={12} sm={8} md={6}>
                    {f.url ? (
                      <Image
                        src={f.url}
                        alt={`frame ${f.seq}`}
                        style={{ borderRadius: 4, width: "100%" }}
                      />
                    ) : (
                      <div
                        style={{
                          height: 120, display: "flex", alignItems: "center",
                          justifyContent: "center", border: "1px dashed #555", borderRadius: 4,
                        }}
                      >
                        <Text type="secondary" style={{ fontSize: 12 }}>not uploaded</Text>
                      </div>
                    )}
                    <div style={{ marginTop: 4 }}>
                      <Space size={4} wrap>
                        <Text type="secondary" style={{ fontSize: 11 }}>#{f.seq}</Text>
                        {f.angle != null && (
                          <AntTooltip title="Door-open angle when this frame was captured">
                            <Tag style={{ marginInlineEnd: 0 }}>∠ {Math.round(f.angle)}°</Tag>
                          </AntTooltip>
                        )}
                        {f.chiller?.confidence != null && (
                          <Tag
                            color={f.chiller.detected ? "green" : "default"}
                            style={{ marginInlineEnd: 0 }}
                          >
                            {f.chiller.confidence.toFixed(2)}
                          </Tag>
                        )}
                        {f.uploadLagMs != null && (
                          <Text type="secondary" style={{ fontSize: 11 }}>
                            +{fmtMs(f.uploadLagMs)}
                          </Text>
                        )}
                      </Space>
                    </div>
                  </Col>
                ))}
              </Row>
            </Image.PreviewGroup>
          </>
        )}
      </Modal>

    </div>
  );
};

export default DoorAnalytics;
